import jwtDefaultConfig from './jwtDefaultConfig'

import router from '@/router'
import ability from '@/libs/acl/ability'
import { initialAbility } from '@/libs/acl/config'
import axios from 'axios'

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  refreshAxios = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // For Refreshing Token
  subscribers = []

  // Token refresh retry management
  refreshRetryCount = 0
  maxRefreshRetries = 3
  refreshRetryDelay = 1000 // Initial delay in ms (will use exponential backoff)
  refreshTimeout = 10000 // 10 seconds timeout for refresh requests

  // Logout deduplication flag
  isLoggingOut = false

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // Create refresh axios with same baseURL but no interceptors
    this.refreshAxios = axios.create({
      baseURL: axiosIns.defaults.baseURL,
      timeout: this.refreshTimeout,
    })

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        // Get token from localStorage
        const accessToken = this.getToken()
        config.headers.SchoolId = process.env.VUE_APP_SCHOOL_ID

        // If token is present add it to request's Authorization Header
        if (accessToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        return config
      },
      error => Promise.reject(error),
    )

    this.axiosIns.interceptors.response.use(
      response => {
        // Reset retry count on successful response
        this.refreshRetryCount = 0
        return response
      },
      error => {
        const { config, response } = error
        const originalRequest = config

        // Only handle 401 errors with valid response
        if (response && response.status === 401) {
          // Prevent marking the same request as retried multiple times
          if (originalRequest._retry || response.data?.needs_login) {
            // This request already failed after token refresh, don't retry again
            console.error('Request failed after token refresh. Logging out.')

            // If needs_login flag is set, immediately clear all queued subscribers
            // to prevent them from sending additional requests with old token
            if (response.data?.needs_login) {
              this.isAlreadyFetchingAccessToken = false
              this.subscribers = []
            }

            this.logoutUser()
            return Promise.reject(error)
          }

          if (!this.isAlreadyFetchingAccessToken) {
            // Check if we've exceeded max retry attempts BEFORE incrementing
            if (this.refreshRetryCount >= this.maxRefreshRetries) {
              console.error('Max token refresh retries exceeded. Logging out user.')
              this.refreshRetryCount = 0
              this.isAlreadyFetchingAccessToken = false
              this.subscribers = [] // Clear any pending subscribers
              this.logoutUser()
              return Promise.reject(new Error('Authentication failed. Please login again.'))
            }

            this.isAlreadyFetchingAccessToken = true
            this.refreshRetryCount++

            // Calculate exponential backoff delay: 1s, 2s, 4s, etc.
            const backoffDelay = this.refreshRetryDelay * Math.pow(2, this.refreshRetryCount - 1)

            // Add delay before retry (exponential backoff)
            return new Promise((resolve, reject) => {
              setTimeout(() => {
                this.refreshToken()
                  .then(r => {
                    this.isAlreadyFetchingAccessToken = false
                    this.refreshRetryCount = 0 // Reset on success

                    // Update accessToken in localStorage
                    this.setToken(r.data.accessToken)

                    // Mark original request as retried
                    originalRequest._retry = true

                    this.onAccessTokenFetched(r.data.accessToken)
                    resolve(this.axiosIns(originalRequest))
                  })
                  .catch(refreshError => {
                    this.isAlreadyFetchingAccessToken = false

                    // If this was the last retry, the refresh endpoint returned
                    // a critical error (401/500), or the request never got a
                    // response at all (network error/timeout), treat it as
                    // unrecoverable and force logout rather than silently
                    // rejecting and leaving the app in limbo.
                    if (this.refreshRetryCount >= this.maxRefreshRetries ||
                        !refreshError.response ||
                        [401, 500].includes(refreshError.response.status)) {
                      console.error('Token refresh failed. Logging out user.')
                      this.refreshRetryCount = 0
                      this.subscribers = [] // Clear any pending subscribers
                      this.logoutUser()
                    }

                    // Notify subscribers that refresh failed
                    this.onAccessTokenFetched(null)
                    reject(refreshError)
                  })
              }, backoffDelay)
            })
          }

          // Queue the original request to retry after token refresh
          return new Promise((resolve, reject) => {
            this.addSubscriber(accessToken => {
              if (!accessToken) {
                // When refresh fails, reject the promise and logout the user
                try {
                  this.logoutUser()
                } catch (logoutError) {
                  console.error('Error during logout:', logoutError)
                }
                return reject(new Error('Token refresh failed'))
              }
              // Mark as retry to prevent infinite loops
              originalRequest._retry = true
              originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
              resolve(this.axiosIns(originalRequest))
            })
          })
        }

        return Promise.reject(error)
      }
    )
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers.forEach(callback => callback(accessToken))
    this.subscribers = []
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  resetRefreshRetryState() {
    this.refreshRetryCount = 0
    this.isAlreadyFetchingAccessToken = false
    this.subscribers = []
  }

  getToken() {
    const token = localStorage.getItem(this.jwtConfig.storageTokenKeyName)
    // Return null if token doesn't exist or is the string "null"
    return token && token !== 'null' ? token : null
  }

  setToken(value) {
    // Only store if value is truthy, otherwise remove the token
    if (value) {
      localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
    } else {
      localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
    }
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
      .then(response => {
        // Reset retry state on successful login
        this.resetRefreshRetryState()
        return response
      })
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  logoutUser() {
    // Prevent multiple concurrent logout calls
    if (this.isLoggingOut) {
      return
    }
    this.isLoggingOut = true

    try {
      localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
      localStorage.removeItem('userData')
      router.push({ name: 'auth-login' })
      ability.update(initialAbility)
    } finally {
      this.isLoggingOut = false
    }
  }

  refreshToken() {
    return this.refreshAxios.post(this.jwtConfig.refreshEndpoint, null, {
      headers: {
        Authorization: `${this.jwtConfig.tokenType} ${this.getToken()}`,
        SchoolId: process.env.VUE_APP_SCHOOL_ID,
      },
    })
  }
}
